import {APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom} from '@angular/core';
import {provideRouter, withHashLocation} from '@angular/router';
import {routes} from './app/app.routing';
import {provideTranslateService, TranslateLoader} from "@ngx-translate/core";
import {HTTP_INTERCEPTORS, HttpClient, HttpClientXsrfModule, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG} from "@ngx-translate/http-loader";
import {BsDropdownModule} from "ngx-bootstrap/dropdown";
import {ModalModule} from "ngx-bootstrap/modal";
import {TabsModule} from "ngx-bootstrap/tabs";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {ToastrModule} from "ngx-toastr";
import {StateService} from "./app/service/state/state.service";
import {StateStorageSyncService} from "./app/service/state/state-storage-sync.service";
import {RxNgZoneSchedulerModule} from "ngx-rxjs-zone-scheduler";
import {HeaderService} from "./app/service/header.service";
import {TextMaskModule} from "angular2-text-mask";
import {DigitlistPipe} from "./app/rma-pipes/digitlist.pipe";
import {RmaDatePipe} from "./app/rma-pipes/rma-date.pipe";
import {RmaCurrencyPipe} from "./app/rma-pipes/rma-currency.pipe";
import {L2DocumentViewerEdgeTypePipe} from "./app/rma-snippets/l2-document-viewer/l2-document-viewer-edge-type.pipe";
import {CommonModule, DatePipe, registerLocaleData} from "@angular/common";
import localeDeCh from '@angular/common/locales/de-CH';
import localeFrCh from '@angular/common/locales/fr-CH';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import {BaseChartDirective} from "ng2-charts";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from '@environments/environment';
import { MessageService } from 'primeng/api';
import { HeaderInterceptor } from './app/service/interceptors/header.interceptor';
import { ResponseStatusInterceptor } from './app/service/interceptors/response-status.interceptor';
import { ErrorMessageInterceptor } from './app/service/interceptors/error-message.interceptor';
import { CSRFTokenInterceptor } from './app/service/interceptors/csrf-token.interceptor.service';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { definePreset } from '@primeng/themes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {EventBusChunkAwareErrorHandler} from "./app/service/handlers/event-bus-chunk-aware-error-handler.service";
import {InactivityTimerConfig} from "./app/models/config/inactivity-timer-config.model";

// TODO: move all theme properties to ibp-prime-ng.theme.scss to keep all config in one place
// Custom IBP theme preset based on Material with custom colors from _ibp-variables.scss
const IbpPreset = definePreset(Material, {
  semantic: {
    primary: {
      50: '#e3f2ff',
      100: '#badeff',
      200: '#8dcaff',
      300: '#5fb6ff',
      400: '#3fa5fe',
      500: '#1283fe',  // $primaryColor from _ibp-variables.scss
      600: '#0B6CD6',
      700: '#0B3F7A',  // $accentColor from _ibp-variables.scss
      800: '#083060',
      900: '#052040',
      950: '#031020'
    },
    colorScheme: {
      light: {
        primary: {
          color: '#1283fe',
          contrastColor: '#ffffff',
          hoverColor: 'rgba(18, 131, 254, 0.92)',
          activeColor: 'rgba(18, 131, 254, 0.68)'
        },
        highlight: {
          background: 'rgba(18, 131, 254, 0.12)',
          focusBackground: 'rgba(18, 131, 254, 0.24)',
          color: '#1283fe',
          focusColor: '#1283fe'
        },
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f4f5f4',  // $backgroundColor from _ibp-variables.scss
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
          950: '#121212'
        }
      }
    }
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            success: {
              background: '#4dbd74',  // $successColor
              hoverBackground: 'rgba(77, 189, 116, 0.92)',
              activeBackground: 'rgba(77, 189, 116, 0.68)',
              color: '#ffffff'
            },
            info: {
              background: '#63c2de',  // $infoColor
              hoverBackground: 'rgba(99, 194, 222, 0.92)',
              activeBackground: 'rgba(99, 194, 222, 0.68)',
              color: '#ffffff'
            },
            warn: {
              background: '#f8cb00',  // $warningColor
              hoverBackground: 'rgba(248, 203, 0, 0.92)',
              activeBackground: 'rgba(248, 203, 0, 0.68)',
              color: '#212529'
            },
            danger: {
              background: '#f86c6b',  // $dangerColor
              hoverBackground: 'rgba(248, 108, 107, 0.92)',
              activeBackground: 'rgba(248, 108, 107, 0.68)',
              color: '#ffffff'
            },
            secondary: {
              background: '#a3a8ab',  // $secondaryColor
              hoverBackground: 'rgba(163, 168, 171, 0.92)',
              activeBackground: 'rgba(163, 168, 171, 0.68)',
              color: '#ffffff'
            }
          }
        }
      }
    }
  }
});


registerLocaleData(localeDeCh, 'de-CH');
registerLocaleData(localeFrCh, 'fr-CH');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeEn, 'en');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withHashLocation()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: IbpPreset,
        options: {
          darkModeSelector: false,
          cssLayer: false
        }
      }
    }),
    { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseStatusInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorMessageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CSRFTokenInterceptor, multi: true },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json'
      }
    },
    provideTranslateService({
      defaultLanguage: 'EN_GB',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    importProvidersFrom(
      BsDropdownModule.forRoot(),
      ModalModule.forRoot(),
      TabsModule.forRoot(),
      TooltipModule.forRoot(),
      ToastrModule.forRoot({
        timeOut: 10000,
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
      }),
      RxNgZoneSchedulerModule,
      TextMaskModule,
      CommonModule,
      BaseChartDirective,
      FontAwesomeModule,
      BrowserAnimationsModule,
      HttpClientXsrfModule,
    ),
    StateService,
    StateStorageSyncService,
    {
      provide: APP_INITIALIZER,
      useFactory: (stateStorageSync: StateStorageSyncService) => () => {
        // This ensures StateStorageSyncService is instantiated and token is loaded from storage
        return Promise.resolve(stateStorageSync.initialStorageValues);
      },
      deps: [StateStorageSyncService],
      multi: true,
    },
    HeaderService,
    DigitlistPipe,
    DatePipe,
    MessageService,
    RmaDatePipe,
    RmaCurrencyPipe,
    L2DocumentViewerEdgeTypePipe,
    {
      provide: ErrorHandler,
      useClass: EventBusChunkAwareErrorHandler,
    },
    importProvidersFrom(RecaptchaV3Module),
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.captchaSiteKey },
    { provide: InactivityTimerConfig, useValue: environment.inactivity_timer_config },
  ],
};
